
import React from 'react';
// Views
import { ModuleGuard } from '../../contexts/SubscriptionContext';
import UsersView from '../../views/admin/users/UsersView';
// retained if used elsewhere
import OrganisationView from '../../views/admin/organisation/OrganisationNetBody';
import InternalControlsView from '../../views/admin/internal-controls/InternalControlsNetBody';
import IncidenceView from '../../views/admin/incidence/IncidenceNetBody';
import MonitoringView from '../../views/admin/monitoring/MonitoringNetBody';
import ComplianceView from '../../views/admin/compliance/ComplianceNetBody';
import AuditView from '../../views/admin/audit/AuditNetBody';
import CapaView from '../../views/admin/capa/CapaNetBody';
import CadenceView from '../../views/application1/CadenceView';
import SettingsView from '../../views/admin/settings/SettingsNetBody';
import { ProjectManagementNetBody as ProjectManagementView } from '../../views/admin/project-management/ProjectManagementNetBody';
import './MainContent.css';
// HomeButton deprecated after breadcrumb rollout
import Breadcrumb from '../navigation/Breadcrumb';
import GovernanceView from '../../views/admin/governance/GovernanceNetBody';
import RiskView from '../../views/admin/risk/RiskNetBody';
// Role-based dashboards
import MemberDashboard from '../../views/member/MemberDashboard';
import AuditorDashboard from '../../views/auditor/AuditorDashboard';
import AnalystDashboard from '../../views/analyst/AnalystDashboard';
import LeadershipPage from '../../pages/LeadershipPage';
import AdminDashboard from '../../views/admin/AdminDashboard';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_ORG_MEMBER, ROLE_AUDIT_USER, ROLE_RISK_ANALYST, ROLE_C_SUITE } from '../../services/userService';
import Sidebar from './Sidebar'; // Import Sidebar

interface MainContentProps {
  activeMenu: string;
  sidebarOpen: boolean;
  onNavigate?: (menuId: string) => void;
  onToggleSidebar?: () => void; // Add onToggleSidebar prop
}

const MainContent: React.FC<MainContentProps> = ({ activeMenu, sidebarOpen, onNavigate, onToggleSidebar }) => {
  const { state } = useAuth();
  // Get role as number from state or per-tab sessionStorage fallback
  const userRoleStr = state.user?.role?.toString() || sessionStorage.getItem('anygrc_user_role');
  const userRole = userRoleStr ? parseInt(userRoleStr, 10) : null;

  const handleMenuSelect = (menuId: string) => {
    if (onNavigate) {
      onNavigate(menuId);
    }
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        // Render different dashboards based on user role (numeric)
        if (userRole === ROLE_ORG_MEMBER) {
          return <MemberDashboard />;
        } else if (userRole === ROLE_AUDIT_USER) {
          return <AuditorDashboard sidebarOpen={sidebarOpen} />;
        } else if (userRole === ROLE_RISK_ANALYST) {
          return <AnalystDashboard />;
        } else if (userRole === ROLE_C_SUITE) {
          return <LeadershipPage />;
        } else {
          // Default GRC Admin dashboard
          return (
            <div className="dashboard-wrapper">
              <AdminDashboard activeMenu={activeMenu} onNavigate={onNavigate} />
              {/* Sidebar is only shown for Admin here if needed, but usually it's global */}
              {/* If we need Sidebar inside MainContent for some reason (e.g. layout), we can add it */}
              {/* But typically Sidebar is outside MainContent in App.tsx */}
              {/* However, the previous code seemed to have Sidebar inside 'dashboard' case for some reason? */}
              {/* Let's check the previous code. It had a Sidebar inside the dashboard case. */}
              {/* If Sidebar is meant to be global, it should be in App.tsx. */}
              {/* But if it's here, we need to pass props. */}
              {/* Wait, the previous code had Sidebar inside the dashboard case for Admin? */}
              {/* Let's assume we need it here for now to match previous behavior, but properly closed tags. */}
              <Sidebar
                isOpen={sidebarOpen}
                onMenuSelect={handleMenuSelect}
                activeMenu={activeMenu}
                onToggle={onToggleSidebar || (() => { })}
              />
            </div>
          );
        }

      case 'users':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'users', label: 'Users', icon: '👥', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About users module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Manage users, actions, roles and rights. Use tabs to switch management aspects.</div>
                    </div>
                  )
                }
              ]}
            />
            <UsersView />
          </div>
        );

      case 'organisation':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'organisation', label: 'Organisation', icon: '🏢', isCurrent: true, after: (
                    <div className="org-help inline" role="note" aria-label="About organisation module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">
                        Manage core organisational data: profile, authority hierarchy, functions, locations and compliance calendar.
                      </div>
                    </div>
                  )
                }
              ]}
            />
            <OrganisationView />
          </div>
        );

      case 'risk':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'risk', label: 'Risk', icon: '⚠️', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About risk module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Identify, assess, register and monitor risks with scoring & mitigation lifecycle.</div>
                    </div>
                  )
                }
              ]}
            />
            <RiskView />
          </div>
        );
      case 'monitoring':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'monitoring', label: 'Monitoring', icon: '📡', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About monitoring module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Automated data ingestion and rule-based detection of risks and control failures.</div>
                    </div>
                  )
                }
              ]}
            />
            <ModuleGuard moduleId="monitoring" label="Continuous Monitoring">
              <MonitoringView />
            </ModuleGuard>
          </div>
        );

      case 'internal-controls':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'internal-controls', label: 'Internal Controls', icon: '🔒', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About internal controls module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Design, catalogue and monitor control activities including testing & remediation.</div>
                    </div>
                  )
                }
              ]}
            />
            <InternalControlsView />
          </div>
        );

      case 'incidence-reporting':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'incidence-reporting', label: 'Incident Reporting', icon: '📝', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About incidence reporting module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Capture, triage, investigate and resolve operational & compliance incidents.</div>
                    </div>
                  )
                }
              ]}
            />
            <IncidenceView />
          </div>
        );

      case 'governance':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'governance', label: 'Governance', icon: '⚖️', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About governance module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Frameworks (policy base), structured meetings (dialogue & decisions) and organisation-wide concurrence (acknowledgement & reservations) lifecycle.</div>
                    </div>
                  )
                }
              ]}
            />
            <GovernanceView />
          </div>
        );

      case 'controls':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'controls', label: 'Controls', icon: '🛡️', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About controls module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Design, implement and evaluate internal controls with testing & monitoring.</div>
                    </div>
                  )
                }
              ]}
            />
            <div className="module-page">
              <div className="module-layout">
                <div className="module-main">
                  <div className="coming-soon">
                    <span className="soon-badge">Coming soon</span>
                    <p>Controls design, assignment, and testing toolkit will appear here.</p>
                  </div>
                </div>
                <aside className="module-aside" aria-label="Controls widgets">
                  <div className="aside-card">
                    <h3>Planned</h3>
                    <p>• Control taxonomy</p>
                    <p>• Test schedule model</p>
                  </div>
                  <div className="sidebar-widget">
                    <h3>Status</h3>
                    <p>• Library schema WIP</p>
                    <p>• Monitoring PoC planned</p>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        );

      case 'compliances':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'compliances', label: 'Compliances', icon: '✅', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About compliances module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Catalogue frameworks, derive obligations, collect evidence and track compliance status.</div>
                    </div>
                  )
                }
              ]}
            />
            <ComplianceView />
          </div>
        );

      case 'audits':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'audits', label: 'Audits', icon: '🧪', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About audits module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Plan engagements, perform fieldwork, record findings and issue reports.</div>
                    </div>
                  )
                }
              ]}
            />
            <AuditView />
          </div>
        );

      case 'capa':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'capa', label: 'CAPA', icon: '🛠️', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About capa module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Corrective & Preventive Actions lifecycle: logging, action tracking, verification and metrics.</div>
                    </div>
                  )
                }
              ]}
            />
            <CapaView />
          </div>
        );

      case 'cadence':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'cadence', label: 'Cadence', icon: '⏱️', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About cadence module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Set the periodicity that drives recurring control tests, compliance filings, monitoring rules and reviews.</div>
                    </div>
                  )
                }
              ]}
            />
            <ModuleGuard moduleId="cadence" label="Cadence">
              <CadenceView />
            </ModuleGuard>
          </div>
        );

      case 'project-management':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'project-management', label: 'Project Management', icon: '💼', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About project management module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Manage projects, tasks, and resources across the portfolio.</div>
                    </div>
                  )
                }
              ]}
            />
            <ModuleGuard moduleId="project-management" label="Project Management">
              <ProjectManagementView />
            </ModuleGuard>
          </div>
        );

      case 'settings':
        return (
          <div className="content-page wide with-home">
            <Breadcrumb
              items={[
                { key: 'home', label: '', icon: '🏠', onClick: () => onNavigate && onNavigate('dashboard') },
                {
                  key: 'settings', label: 'Settings', icon: '⚙️', isCurrent: true, after: (
                    <div className="module-help" role="note" aria-label="About settings module">
                      <span className="info-icon" tabIndex={0} aria-label="About this section">i</span>
                      <div className="help-tooltip" role="tooltip">Configure organisation profile, roles, integrations and branding options.</div>
                    </div>
                  )
                }
              ]}
            />
            <SettingsView />
          </div>
        );

      default:
        return (
          <div className="content-page">
            <div className="page-header">
              <h1>Welcome to AnyGRC</h1>
              <p>Select a menu item to get started</p>
            </div>
          </div>
        );
    }
  };

  const isAuditUser = userRole === ROLE_AUDIT_USER;

  return (
    <main className={`main-content ${sidebarOpen ? 'sidebar-open' : ''} ${isAuditUser ? 'audit-mode' : ''}`}>
      {renderContent()}
    </main>
  );
};

export default MainContent;